import { effect, signal } from '@preact/signals';

/**
 * Les chaînes, **hors du code**.
 *
 * La V1 était bilingue ; la V2 avait le français en dur. Le sortir maintenant
 * coûte quarante chaînes ; le sortir à la fin de l'étape 4 en coûterait six cents,
 * et chacune aurait déjà été relue, testée et traduite dans un commentaire.
 *
 * Pas de bibliothèque : un dictionnaire plat, une interpolation `{nom}`, et une
 * clé manquante qui **rend la clé** plutôt que du vide — une chaîne absente doit
 * se voir à l'écran, pas s'y effacer.
 *
 * **Le défaut est le français, quelle que soit la langue du navigateur.** C'est un
 * choix, pas un oubli : le produit est écrit en français, sa page d'accueil, ses
 * messages d'exploitation et ses tests de bout en bout le sont aussi. L'anglais
 * est un choix explicite, mémorisé par appareil.
 */

export type Locale = 'fr' | 'en';

const KEY = 'synkara.locale';

const fr: Record<string, string> = {
  'app.name': 'Synkara',
  'app.tagline':
    'Un espace à vous pour vos photos, vos documents et vos notes. Ce que vous partagez part vers des cercles que vous nommez — et vous pouvez le reprendre.',
  // Le titre d'accueil se lit en deux temps : ce qu'on garde, puis ce que
  // personne d'autre ne montre — **qui le voit**. La seconde moitié est en
  // italique et porte l'accent, parce que c'est la thèse du produit.
  'app.promiseHead': 'Tout ce que vous gardez, et',
  'app.promiseTail': 'qui peut le voir.',
  'app.signIn': 'Se connecter',
  'app.signOut': 'Se déconnecter',
  'app.loadingAccount': 'Chargement de votre compte…',
  'app.signingIn': 'Connexion en cours…',
  'app.syncing': 'Synchronisation…',
  'app.syncingCount': 'Synchronisation — {count} élément reçu|Synchronisation — {count} éléments reçus',
  'app.sessionExpired': 'Session expirée.',
  // **Elle manquait aux deux dictionnaires** *(2026-08-16)*, alors que
  // `shared.tsx` l'appelait depuis le 2026-08-06 : la confirmation de suppression
  // d'un groupe affichait donc un bouton libellé « app.cancel ». Une clé absente
  // rend la clé — c'est le comportement voulu, et c'est aussi ce qui fait qu'un
  // oubli traverse une relecture sans lever la main.
  'app.cancel': 'Annuler',

  'nav.space': 'Mon espace',
  'nav.space.q': 'Où est-ce que je l’ai rangé ?',
  'nav.shared': 'Mes partages',
  'nav.shared.q': 'Qu’est-ce qui circule, et avec qui ?',
  'nav.me': 'Moi',
  // **Une remontée, pas un historique** : le bouton va au niveau au-dessus, là
  // où le geste système défait le dernier pas. Les deux coexistent partout.
  'nav.back': 'Revenir',

  'header.storage': 'Stockage',
  // **La fraîcheur était un geste manquant** *(2026-08-16)*. `pullChanges` n'avait
  // que deux appelants — le démarrage et la sortie d'un groupe —, donc un onglet
  // ouvert ne voyait **jamais** ce que les autres avaient fait. Le libellé dit
  // ce qu'on va chercher, pas le mécanisme : « synchroniser » nommerait le
  // journal `/changes`, qui n'est l'affaire de personne.
  'header.refresh': 'Actualiser',
  'header.refreshing': 'Recherche des nouveautés…',
  'header.theme.light': 'Thème clair',
  'header.theme.dark': 'Thème sombre',
  'header.theme.system': 'Thème du système',
  'header.language': 'Langue',
  'header.identifier': 'Identifiant',
  'header.account': 'Compte de {name}',

  'state.offline': 'Hors ligne — vous voyez la copie locale, les modifications attendront.',
  'state.emptyFolder': 'Ce dossier est vide.',
  'state.emptyGallery': 'Aucun fichier pour l’instant.',
  'state.emptyGroups': 'Vous n’êtes membre d’aucun groupe.',
  'state.emptyFeed': 'Rien de partagé pour l’instant.',

  // Les libellés ont perdu leur « + » : il est porté **une seule fois**, par le
  // bouton qui ouvre le menu. Le répéter sur chaque ligne laissait croire à
  // plusieurs points d'ajout — ce qu'il y avait, justement, et qu'on corrige.
  'add.label': 'Ajouter',
  // Les trois portes d'envoi, dans l'ordre de la feuille d'ajout de l'app. Chacune
  // porte l'`accept` de ce qu'elle promet : sans lui, Chrome/Android propose
  // l'enregistreur audio et demande le microphone à qui envoyait une photo.
  'add.camera': 'Prendre une photo',
  'add.media': 'Photos et vidéos',
  'add.documents': 'Documents',
  'space.root': 'Racine',
  'space.folders': 'Dossiers',
  'space.newFolder': 'Nouveau dossier',
  'space.newNote': 'Nouvelle note',
  'space.deleteFolder': 'Supprimer ce dossier',
  /* Le bouton dit **ce qu'on obtiendra**, jamais l'état où l'on est. */
  // **L'axe de lecture**, et non deux destinations : « Mes fichiers » a cessé
  // d'être un lieu le 2026-08-09 — il n'en restait qu'un ordre de lecture.
  'space.axis': 'Façon de regarder',
  'space.axis.folder': 'Dossiers',
  'space.axis.all': 'Tout',
  'space.axis.allQ': 'Qu’est-ce que j’ai ?',
  // Le segment *Dossiers* déploie l'arborescence : c'est **le même contrôle** qui
  // porte l'axe et le dossier, parce que le dossier courant *est* la position sur
  // cet axe. Le nom du dossier entre dans le libellé accessible — sans lui, deux
  // boutons voisins s'annonceraient « Dossiers ».
  'space.chooseFolder': 'Dossier : {name} — changer de dossier',
  'space.display': 'Affichage',
  // **Un bouton, quatre positions** : le libellé nomme l'état courant, jamais le
  // suivant — sur un cycle, savoir où l'on est suffit à savoir où l'on va.
  'space.displayCycle': 'Affichage : {current} — changer',
  'space.layout': 'Présentation',
  'space.densityGroup': 'Taille des vignettes',
  'space.countDetail': '{own} ici, {total} avec les sous-dossiers',
  'space.density.large': 'Grandes vignettes',
  'space.density.medium': 'Vignettes moyennes',
  'space.density.small': 'Petites vignettes',
  'space.layout.auto': 'Automatique',
  'space.layout.list': 'En liste',
  'space.layout.grid': 'En vignettes',
  'space.expand': 'Déplier {name}',
  'space.collapse': 'Replier {name}',
  'space.folderPlaceholder': '/vacances/2026',
  'space.notePlaceholder': 'Nom de la note',
  'space.create': 'Créer',
  'space.upload': 'Envoyer des fichiers',
  'space.delete': 'Supprimer',
  // Le nom est **dans** la question : une confirmation qui ne dit pas sur quoi
  // elle porte s'acquitte sans être lue, et c'est justement ce qu'on veut
  // empêcher.
  // **Elle disait le contraire de ce que le produit fait** *(2026-09-12)*. « Il ne
  // sera pas récupérable » datait d'avant P2, qui a posé une corbeille ; la page
  // Corbeille annonçait « 30 jours » à deux écrans de là. Une confirmation qui
  // exagère le danger se paie deux fois : elle retient un geste réversible, et
  // elle décrédibilise les avertissements qui, eux, sont vrais.
  //
  // Le nombre de jours est **servi** par `GET /me`, jamais écrit ici : c'est la
  // règle des plafonds, et la rétention est un plafond comme un autre.
  'space.confirmDelete': 'Supprimer « {name} » ? Il partira à la corbeille, où il reste {days} jours.',
  // **Sans nombre**, quand `/me` n'a pas encore répondu : dire « il partira à la
  // corbeille » est vrai et suffit, alors qu'inventer un nombre recréerait la
  // seconde source de vérité qu'on vient de retirer.
  'space.confirmDeleteSoft': 'Supprimer « {name} » ? Il partira à la corbeille.',
  'space.creatingFolder': 'Création du dossier…',
  'space.creatingNote': 'Création de la note…',
  'space.deletingFolder': 'Suppression du dossier…',
  'space.deleting': 'Suppression…',
  'space.uploading': 'Envoi…',
  'space.checkingDuplicates': 'Recherche des doublons ({index}/{total})…',
  'import.duplicates':
    '{count} doublon a été trouvé et ne sera pas importé.|{count} doublons ont été trouvés et ne seront pas importés.',
  'import.duplicatesInTrash': 'Dont {count} dans la corbeille.',
  'import.duplicatesTitle': 'Doublons trouvés',
  'import.skipDuplicates': 'OK',
  'import.force': 'Importer quand même',
  'space.uploadingOne': 'Envoi de « {name} » ({index}/{total})…',
  // **Les octets, pas seulement le rang** *(2026-09-12)*. « 2/10 » ne bouge pas
  // pendant les quarante secondes que met une vidéo à remonter : le retour d'usage
  // disait « on attend sans rien voir bouger ». La phrase porte les octets, et le
  // `<progress>` de `Notice` porte la même valeur en continu.
  'space.uploadingBytes': 'Envoi de « {name} » ({index}/{total}) — {sent} sur {size}…',
  // Un envoi qui recommence du début sans le dire se lit comme une page bloquée.
  'space.resumingOne': 'Reprise de l’envoi de « {name} » ({index}/{total})…',
  // **`space.processing` a disparu** *(2026-09-12)*. Elle était posée dans `busy`,
  // qui **désactive tous les boutons du site** : l'écran affichait « Traitement des
  // images… » et refusait tout geste pendant que 56 dérivés se construisaient. Le
  // compte ci-dessous la remplace, hors de `busy`, donc sans rien bloquer.
  'space.processingCount': '{count} fichier en cours de traitement…|{count} fichiers en cours de traitement…',

  'viewer.close': 'Fermer',
  'viewer.previous': 'Précédent',
  'viewer.next': 'Suivant',
  'viewer.unavailable': 'Ces octets ne sont pas disponibles.',
  'viewer.fullscreen': 'Plein écran',
  'viewer.exitFullscreen': 'Quitter le plein écran',

  'reader.emptyNote': 'Cette note est vide — elle n’a pas encore de contenu.',
  'reader.noBytes': 'Ce document n’a pas encore d’octets constatés.',
  'reader.notPreviewable': 'Aucun aperçu pour un fichier {format}.',

  'editor.write': 'Écrire',
  // La sortie de secours dit **ce qu'on perd**, jamais « êtes-vous sûr ? » : la
  // question ne se pose qu'à celui qui sait déjà ce qu'il risque.
  'editor.leaveUnsaved': 'Vos modifications n’ont pas pu être enregistrées.',
  'editor.leaveDiscard': 'Fermer sans enregistrer',
  'editor.stay': 'Rester',
  'editor.body': 'Corps de la note',
  // **Deux modes, plus un aperçu.** L'éditeur en clair *est* l'aperçu depuis le
  // 2026-08-09 ; ce qui reste en face de lui n'est pas une prévisualisation mais
  // la source, c'est-à-dire ce qui sera réellement enregistré.
  'editor.modeRich': 'Édition en clair',
  'editor.modeSource': 'Afficher le Markdown',
  'editor.format': 'Mise en forme',
  // Le point d'entrée des gestes rares de l'éditeur : la vue du Markdown et
  // l'historique. Importants, mais pas permanents — ils prenaient à
  // « Enregistrer » la place qui lui manquait sur un téléphone.
  'editor.more': 'Plus d’actions',
  'editor.bold': 'Gras',
  'editor.italic': 'Italique',
  'editor.strike': 'Barré',
  'editor.heading': 'Titre {level}',
  'editor.paragraph': 'Paragraphe',
  'editor.bulletList': 'Liste à puces',
  'editor.numberList': 'Liste numérotée',
  'editor.todo': 'Case à cocher',
  'editor.quote': 'Citation',
  'editor.codeBlock': 'Bloc de code',
  'editor.rule': 'Séparateur',
  'editor.link': 'Lien',
  'editor.linkUrl': 'Adresse du lien',
  'editor.linkApply': 'Poser',
  'editor.linkRemove': 'Retirer le lien',
  'editor.table': 'Tableau',
  'editor.tableTools': 'Tableau',
  'editor.rowBefore': 'Ligne au-dessus',
  'editor.rowAfter': 'Ligne en dessous',
  'editor.colBefore': 'Colonne à gauche',
  'editor.colAfter': 'Colonne à droite',
  'editor.dropRow': 'Supprimer la ligne',
  'editor.dropCol': 'Supprimer la colonne',
  'editor.dropTable': 'Supprimer le tableau',
  'editor.image': 'Image',
  'editor.imageTools': 'Image',
  'editor.imageFromSpace': 'Choisir dans mon espace',
  'editor.imageUpload': 'Envoyer une photo',
  'editor.imageAlt': 'Texte de remplacement',
  'editor.imageAltHint': 'Décrire l’image…',
  'editor.imageRemove': 'Retirer l’image',
  'editor.imageLoading': 'Chargement de vos photos…',
  'editor.imageNone': 'Aucune photo dans votre espace pour l’instant.',
  'editor.imagePickFailed': 'Vos photos n’ont pas pu être chargées.',
  'editor.opening': 'Ouverture de la note…',
  'editor.save': 'Enregistrer',
  'editor.saving': 'Enregistrement…',
  // **Rien à faire n'est pas un impératif** : le bouton reste, parce qu'on peut
  // toujours forcer un enregistrement, mais il cesse de réclamer.
  'editor.saved': 'Enregistré',
  'editor.unsaved': 'Modifié',
  'editor.revision': 'Révision {rev}',
  'editor.reload': 'Recharger',
  'editor.reloading': 'Rechargement…',
  'editor.retry': 'Réessayer',
  'editor.keepMine': 'Garder mon texte',
  'editor.history': 'Historique',
  'editor.historyEmpty': 'Aucune révision : cette note n’a jamais été enregistrée.',
  'editor.loadingHistory': 'Chargement de l’historique…',
  'editor.restore': 'Restaurer',
  'editor.restoring': 'Restauration…',
  // Les quatre conflits, et chacun dit **quoi faire** — pas seulement ce qui rate.
  'editor.conflictStale':
    'Cette note a été enregistrée ailleurs (révision {rev}). Rechargez, ou gardez votre texte pour l’enregistrer par-dessus.',
  'editor.conflictLocked': 'Quelqu’un édite cette note en ce moment.',
  'editor.conflictLockedUntil': 'Quelqu’un édite cette note ; réessayez après {at}.',
  'editor.conflictLockLost':
    'Votre verrou a été repris pendant l’enregistrement (révision {rev}). Rechargez avant de réessayer.',
  'editor.conflictMissingBytes':
    'Le serveur n’a pas constaté les octets envoyés. Réessayez l’enregistrement.',

  'inspector.title': 'Détails',
  'inspector.unavailable': 'Fiche indisponible.',
  'inspector.name': 'Nom',
  'inspector.rename': 'Renommer',
  'inspector.renaming': 'Renommage…',
  'inspector.folder': 'Dossier',
  'inspector.moving': 'Déplacement…',
  'inspector.kind': 'Nature',
  // **À qui c'est.** Les gestes d'un objet dépendent de la réponse ; sans elle,
  // un bouton absent se lit comme un bogue plutôt que comme un droit.
  'inspector.owner': 'Propriétaire',
  'inspector.ownerMe': 'Vous',
  'inspector.ownerOther': 'Quelqu’un d’autre',
  // **Deux dates, deux questions.** « Date » est celle du document — la prise de
  // vue pour une photo, ce que R4 remplit depuis l'EXIF ; « Ajouté le » est celle
  // de l'arrivée dans l'espace. Les confondre faisait passer une photo de 2019
  // pour une photo du jour.
  'inspector.date': 'Date',
  'inspector.uploaded': 'Ajouté le',
  'inspector.camera': 'Appareil',
  // **R4** : la position de prise de vue. Elle n'avait aucun écran jusqu'ici, et
  // depuis que le worker la remplit pour ce qui part du site, la taire serait
  // garder une donnée sensible que son propriétaire ne peut pas voir.
  'inspector.location': 'Lieu',
  'inspector.size': 'Taille',
  'inspector.format': 'Format',
  'inspector.revision': 'Révision',
  'inspector.pending': 'à venir',
  'inspector.download': 'Télécharger',
  // **Le rôle se nomme par ce qu'il sert**, jamais par sa clé technique : on
  // choisit entre « une image d'écran » et « le fichier tel qu'il est arrivé »,
  // pas entre `standard` et `source`.
  'inspector.role.standard': 'Version écran',
  'inspector.role.source': 'Fichier d’origine',
  'inspector.role.content': 'Texte de la note',
  // Ce qui explique pourquoi la source n'est pas le défaut : passé trente jours,
  // elle dort en archive instantanée, dont la récupération se facture.
  'inspector.roleArchived': 'archivé',
  'inspector.preparing': 'Préparation du téléchargement…',
  'inspector.votes': '{count} avis|{count} avis',
  'inspector.open': 'Ouvrir',

  // **R6 — voir, corriger, régénérer** *(2026-09-13)*. « Décrit automatiquement »
  // se lit à côté du champ tant que la machine le tient : corriger le lui retire,
  // et c'est ce qui protège une correction d'un rattrapage.
  'inspector.description': 'Description',
  'inspector.descriptionPlaceholder': 'Ajouter une description',
  'inspector.tags': 'Mots-clés',
  'inspector.tagAdd': 'Ajouter un mot-clé',
  'inspector.tagRemove': 'Retirer « {tag} »',
  'inspector.save': 'Enregistrer',
  'inspector.saving': 'Enregistrement…',
  'inspector.machine': 'décrit automatiquement',
  'describe.regenerate': 'Régénérer la description',
  'describe.requesting': 'Demande de description…',
  // **Q3 — « Régénérer » écrase, et il le dit avant.** La question ne paraît que
  // s'il y a une correction à perdre ; sans elle, le geste part directement.
  'describe.confirm': 'Remplacer vos corrections ?',
  'describe.confirmNote': 'La description et les mots-clés que vous avez modifiés seront remplacés par ceux du modèle.',
  'describe.confirmVerb': 'Remplacer',
  'describe.queued': 'Description demandée : elle arrive dans quelques instants.',
  'describe.pending': 'Pas encore décrite.',

  // **L'affiche d'une vidéo** (M6). Le mot « affiche » plutôt que « vignette » :
  // la vignette, c'est la planche entière, et ce qu'on choisit ici est la case
  // qui la représente — celle que les autres verront dans leur fil.
  'poster.title': 'Affiche',
  'poster.help': 'La case qui représente cette vidéo, ici et pour ceux avec qui vous la partagez.',
  'poster.frame': 'Case {n}',
  'poster.reset': 'Première case',
  'poster.saving': 'Enregistrement de l’affiche…',

  'kind.note': 'note',
  'kind.doc': 'document',
  'kind.photo': 'photo',
  'kind.video': 'vidéo',
  'kind.collection': 'partage · {count} élément|partage · {count} éléments',

  'gallery.filters': 'Filtres',
  'gallery.type': 'Type',
  'gallery.any': 'Tous',
  'gallery.images': 'Photos',
  'gallery.videos': 'Vidéos',
  'gallery.notes': 'Notes',
  'gallery.docs': 'Documents',
  'gallery.minScore': 'Note minimale',
  'gallery.camera': 'Appareil',
  // **Les deux critères de R3** *(2026-09-12)*. « Aucun » n'est pas « Tous » : il
  // répond à *qu'est-ce qui ne circule pas encore ?*, la question qu'on se pose
  // dans un dossier dont on a partagé une partie.
  'gallery.share': 'Partage',
  'gallery.shareNone': 'Dans aucun partage',
  'gallery.owner': 'Propriétaire',
  // « Moi » plutôt que son propre alias : dans un partage collectif, on ne doit
  // pas avoir à se reconnaître dans une liste pour isoler ses éléments.
  'gallery.ownerMe': 'Moi',
  'gallery.ownerUnknown': 'Sans nom',
  'gallery.emoji': 'Réactions',
  'gallery.sort': 'Tri',
  'gallery.sortNewest': 'Plus récentes',
  'gallery.sortOldest': 'Plus anciennes',
  'gallery.sortKind': 'Type puis nom',
  'gallery.sortName': 'Nom',
  'gallery.sortSize': 'Taille',
  'gallery.duplicates': 'Doublons',
  'gallery.duplicatesOn': 'Doublons ({count})',
  'gallery.duplicateThreshold': 'Ressemblance : {n}',
  'gallery.duplicateThresholdHint': '0–2 : vrais doublons · au-delà : photos proches',
  'gallery.duplicateOf': 'Doublon probable — {count} photo dans le groupe|Doublon probable — {count} photos dans le groupe',
  'gallery.clearFilters': 'Effacer les filtres',
  'gallery.atLeast': '{count} étoile et plus|{count} étoiles et plus',
  'gallery.count': '{shown} sur {total}',
  'gallery.localScope': 'les filtres portent sur ce qui est synchronisé',
  // **R7 — la recherche, et sa limite écrite dans l'écran (C6).** Elle porte sur
  // ce qui est synchronisé, et sur des mots : sans la seconde moitié, une photo de
  // plage introuvable par « bord de mer » se lirait comme une photo absente.
  'search.label': 'Rechercher',
  'search.placeholder': 'Chercher : chien, plage, août 2026…',
  'search.none': 'Rien ne correspond à « {query} » ici.',
  'search.limit': 'La recherche porte sur ce qui est synchronisé, et sur les mots écrits : « bord de mer » ne trouve pas « plage ».',
  'gallery.pick': 'Sélectionner',
  'gallery.inspect': 'Détails',
  // **« Envoyer », jamais « Partager »** : dans Synkara, « partage » désigne le
  // partage à un cercle, et `vocabulaire.spec.ts` l'impose. Le geste d'ici sort
  // de l'application ; le verbe doit le dire.
  'send.action': 'Envoyer',
  'send.preparing': 'Préparation de l’envoi…',
  'send.expired':
    'La feuille de partage ne s’est pas ouverte à temps. Réessayez : les données sont prêtes.',
  'send.unsupported': 'Ce navigateur ne sait pas envoyer ce fichier.',
  'send.noBytes': 'Les octets n’ont pas pu être récupérés ({status}).',
  // Le code se recopie, une phrase ne se recopie pas : c'est déjà la règle de
  // `error.generic`, et c'est ce qui rend un incident rapportable.
  'send.failed': 'L’envoi a échoué ({code}).',
  'news.title': 'Nouveautés',
  'news.lead':
    'Ce qui a changé dans Synkara, de la version la plus récente à la plus ancienne. Vous utilisez la version {version}.',
  'send.link': 'Envoyer un lien',
  'send.linkWhich': 'Le lien mène à quel partage ?',
  'link.opening': 'Ouverture du lien…',
  'link.unavailable':
    'Ce lien ne mène à rien que vous puissiez voir : le partage a pu être retiré, ou ne vous est pas ouvert.',
  'link.photoGone': 'Cette photo n’est plus dans ce partage.',
  'gallery.selection': 'Sélection',
  'gallery.selectAll': 'Tout sélectionner ({count})',
  'gallery.picked': '{count} sélectionné|{count} sélectionnés',
  'gallery.clearSelection': 'Annuler la sélection',
  'gallery.deletePicked': 'Supprimer',
  // **Le geste destructeur passe derrière un repli** *(2026-08-16)*. Il était le
  // troisième bouton d'une barre qui en compte quatre, et il partait **sans
  // confirmation** : un doigt posé un centimètre à côté effaçait la sélection
  // entière. Les autres gestes de la barre restent visibles — ils ne détruisent
  // rien, et les enfouir aurait rendu la barre inutile pour se protéger d'un
  // seul d'entre eux.
  'gallery.moreActions': 'Autres actions',
  'gallery.confirmDeletePicked':
    'Supprimer {count} élément ? Il partira à la corbeille, où il reste {days} jours.|Supprimer {count} éléments ? Ils partiront à la corbeille, où ils restent {days} jours.',
  'gallery.confirmDeletePickedSoft':
    'Supprimer {count} élément ? Il partira à la corbeille.|Supprimer {count} éléments ? Ils partiront à la corbeille.',
  'gallery.deletingSelection': 'Suppression de {count} élément…|Suppression de {count} éléments…',
  'gallery.noThumb': 'Vignette indisponible',

  // **Le mot d'écran est « partage »** *(2026-08-16)* — jamais « collection »,
  // qui ne disait rien à personne. Les clés gardent leur préfixe : ce sont des
  // identifiants, et le socle, lui, dit `album` (routes, modèle). Le mot ne
  // heurte pas la destination *Mes partages*, il la prolonge — elle est
  // justement la liste des partages. « Partage » est **masculin** : tous les
  // accords de ces phrases en dépendent.
  'collection.title': 'Partage',
  'collection.loading': 'Ouverture du partage…',
  'collection.creating': 'Création du partage…',
  'collection.adding': 'Ajout au partage…',
  'collection.removing': 'Retrait du partage…',
  'collection.empty': 'Ce partage est vide.',
  'collection.addTo': 'Ajouter à un partage…',
  'object.actions': 'Actions sur {name}',
  'collection.add': 'Ajouter',
  'collection.newName': 'Nouveau partage',
  'collection.createAndAdd': 'Créer et ajouter',
  // **Le sélecteur passe par le groupe** *(2026-08-16)*. Il alignait tous les
  // partages de tous les groupes sur une liste plate — tenable à trois, illisible
  // à trente. Le groupe est de toute façon la première question : ajouter à un
  // partage, c'est donner à son audience.
  'collection.pickGroup': 'À quel groupe ?',
  'collection.backToGroups': 'Tous les groupes',
  'collection.shareCount': '{count} partage|{count} partages',
  // Le dernier partage servi est proposé d'emblée — c'est le cas de très loin le
  // plus fréquent —, mais il est **nommé comme tel** : sans ce mot, on croirait
  // que le sélecteur a choisi pour nous.
  'collection.lastUsed': 'Dernier utilisé',
  // **R3** : le sélecteur reste ouvert pour verser dans plusieurs partages.
  //
  // Les trois titres de section portent tout le poids de l'explication
  // *(2026-09-12)* : « on n'a pas compris comment on faisait pour ajouter à
  // plusieurs partages ». Rien ne l'annonçait, et le sélecteur se fermant au clic
  // jusque-là, le comportement appris était l'inverse. Le mot **autre** est ce qui
  // dit que le geste se répète, sans phrase d'explication.
  'collection.inShares': 'Déjà dans ces partages',
  'collection.addToExisting': 'Ajouter à un partage existant',
  'collection.addToAnother': 'Ajouter à un autre partage existant',
  'collection.createSection': 'Créer un nouveau partage',
  'collection.allAlreadyIn': 'Tous les partages de ce groupe les contiennent déjà.',
  'collection.addedTo': 'Terminé — {count} partage|Terminé — {count} partages',
  'collection.remove': 'Retirer',
  // **La phrase descend dans la confirmation** *(2026-08-16)*. Posée sous le
  // geste, elle répondait à une question qu'on ne se pose pas encore : au moment
  // de lire un menu, on cherche un verbe, pas une explication. Le menu ne porte
  // donc plus que « Retirer », et la phrase se lit **là où l'on décide** —
  // laquelle décision existe désormais, ce qui n'était pas le cas.
  'collection.removeIsNotDelete':
    'Retirer un élément d’un partage ne le supprime pas : il reste rangé dans son dossier.',
  'collection.confirmRemove': 'Retirer cet élément du partage ?',
  'collection.someRefused': '{count} élément refusé ({code}).|{count} éléments refusés ({code}).',
  // La phrase disait « une collection est faite pour être partagée » ; avec le
  // mot juste elle se mordrait la queue. Elle dit donc la **règle** plutôt que
  // la définition : un partage a toujours une audience.
  'collection.needsGroup': 'Un partage va toujours à un groupe : choisissez lequel.',
  'collection.group': 'Partagé avec',
  'collection.none': 'Aucun partage dans ce groupe.',
  'collection.itemCount': '{count} élément|{count} éléments',
  'collection.deleting': 'Suppression du partage…',
  'collection.delete': 'Supprimer le partage',
  'collection.confirmDelete':
    'Supprimer le partage ? Les membres perdent l’accès à ce qu’il exposait ; les objets, eux, restent dans votre espace.',
  'collection.savingPolicy': 'Enregistrement…',
  // **Le réglage se lit, il ne se devine plus** *(2026-08-16)*. Il tenait dans le
  // nom accessible d'un bouton-icône — donc nulle part au doigt. Le libellé dit
  // l'autorisation, l'indication dit la conséquence, et elle **change avec
  // l'état** : c'est la seconde phrase qui apprend au propriétaire que ses notes
  // deviennent modifiables, ce qu'aucune infobulle ne disait.
  'collection.contribToggle': 'Autoriser les membres à contribuer',
  // **R3** : dans un menu, le libellé est un verbe — ce que le clic fait, jamais
  // l'état qu'on quitte. L'état se lit au-dessus de la grille.
  'collection.contribRestrict': 'Réserver l’ajout au propriétaire',
  'collection.admin': 'Administrer ce partage',
  'collection.contribOn': 'Les membres peuvent y ajouter leurs objets et modifier ses notes.',
  'collection.contribOff': 'Vous seul pouvez y ajouter des objets.',
  'collection.collectiveNotice':
    'Partage collectif : vous pouvez y ajouter vos propres objets et modifier ses notes.',
  'collection.collective': 'Collectif',
  'collection.readOnlyNote': 'Cette note ne vous est pas ouverte en écriture.',

  'reaction.title': 'Réactions',
  'reaction.score': 'Note',
  'reaction.emoji': 'Emoji',
  'reaction.stars': '{count} étoile|{count} étoiles',
  'reaction.average': 'Note moyenne',
  'reaction.clear': 'Retirer ma réaction',
  'reaction.saving': 'Enregistrement de la réaction…',
  'reaction.removing': 'Retrait de la réaction…',
  'reaction.who': 'Qui a réagi',
  'reaction.loadingPeople': 'Lecture des réactions…',
  'reaction.nobody': 'Personne n’a encore réagi.',
  'reaction.moderate': 'Retirer la réaction de {name}',
  'reaction.moderating': 'Retrait de la réaction…',

  'entity.pendingUpload': 'envoi en attente',
  'entity.processing': 'traitement en cours',
  'entity.failed': 'échec du traitement',

  // **Le motif d'un échec, nommé** *(2026-09-12)*.
  //
  // `FailReason` voyageait dans chaque réponse depuis l'étape 1 et aucun client ne
  // le lisait : 42 photos ont échoué en production sous un « échec du traitement »
  // qui ne disait rien, et la première passe de diagnostic n'a rien pu apporter de
  // plus que le mot « échec ». C'est la règle d'`error.generic` appliquée au
  // pipeline — **le code est dans le message dès la première écriture**.
  //
  // Chaque motif dit ce qui s'est passé **et** ce qu'on peut faire, parce que les
  // deux n'ont pas la même réponse : une source trop grande se remplace, un
  // `derivatives_failed` se retente.
  'fail.pixels_exceeded':
    'Image trop grande pour être traitée : elle dépasse la limite de définition. Une version réduite passera.',
  'fail.format_unknown':
    'Format non reconnu : ces octets ne correspondent à aucun type d’image ou de vidéo connu.',
  'fail.format_rejected':
    'Format refusé pour cette nature de fichier — par exemple une vidéo arrivée par le chemin des images.',
  'fail.dimensions_unknown':
    'Dimensions illisibles : le fichier est peut-être tronqué ou incomplet.',
  'fail.derivatives_failed':
    'Le traitement a échoué côté serveur. La source est intacte : « Retenter » suffit le plus souvent.',
  // Un motif que ce client ne connaît pas se rend **brut**. Le taire ferait
  // regresser vers le « échec » muet que cet incrément corrige ; un code brut,
  // lui, se recopie dans un rapport.
  'fail.unknown': 'Motif : {reason}',
  'entity.failedBecause': '{label} — {reason}',

  'entity.retry': 'Retenter le traitement',
  'entity.retrying': 'Nouvelle tentative…',
  // La source n'a pas à repartir : elle est déjà dans le stockage, et le serveur
  // se contente de reconstater ses octets puis de réenfiler le job.
  'entity.retryHint': 'Les octets sont déjà envoyés — seuls les dérivés sont reconstruits.',

  'shared.members': '{count} membre|{count} membres',
  // Les rôles arrivent en minuscules de `GET /groups` (`owner`, `member`).
  'shared.role.owner': 'propriétaire',
  'shared.role.member': 'membre',

  // Les groupes (4.6). Le vocabulaire est celui des gens, jamais celui du modèle :
  // on « invite » quelqu'un, on ne « crée pas une appartenance ».
  'group.new': 'Créer le groupe',
  'group.newName': 'Nom du groupe',
  'group.creating': 'Création du groupe…',
  'group.rename': 'Renommer le groupe',
  'group.renaming': 'Renommage…',
  'group.delete': 'Supprimer le groupe',
  'group.deleting': 'Suppression du groupe…',
  'group.confirmDelete':
    'Le groupe et l’accès de tous ses membres disparaissent. C’est définitif.',
  'group.leave': 'Quitter le groupe',
  'group.leaving': 'Sortie du groupe…',
  'group.confirmLeave': 'Vous perdrez l’accès à tout ce qui y est partagé.',
  'group.collections': 'Partages',
  'group.collectionCount': '{count} partage|{count} partages',
  'group.feed': 'Nouveautés',
  'group.itemCount': '{count} élément|{count} éléments',
  'group.fresh': '{count} cette semaine',
  'group.seeAll': 'Tout voir',
  'group.members': 'Membres',
  'group.invite': 'Inviter',
  'group.invitePlaceholder': 'Adresse e-mail',
  'group.inviting': 'Invitation…',
  'group.remove': 'Retirer {name} du groupe',
  'group.removing': 'Retrait…',
  'group.confirmRemove': 'Retirer',
  'group.unnamed': 'Sans nom',
  'group.you': 'vous',
  'group.someone': 'Quelqu’un',
  'group.emptyFeed': 'Rien n’a encore circulé dans ce groupe.',
  'group.aloneHere': 'Vous êtes seul dans ce groupe.',
  'group.revoked': 'retiré',
  'group.more': 'Voir plus',

  // « Où va ma donnée » (D15) — la question a toujours un objet direct.
  'share.where': 'Où va cette donnée',
  'share.loading': 'Lecture des partages…',
  'share.nowhere': 'Cet objet n’est partagé nulle part.',
  // Le cas `direct` ne dit plus « partage direct » : dans une liste où l'autre
  // entrée est « par un partage », les deux mots se confondraient. Il n'existe
  // d'ailleurs aucune route qui expose une entité seule (D15).
  'share.viaDirect': 'accès direct',
  'share.viaCollection': 'par un partage',
  'share.revoke': 'Couper l’accès de {name}',
  'share.revoking': 'Retrait de l’accès…',
  'share.exposedTo': 'Partagé avec {count} groupe|Partagé avec {count} groupes',
  'share.blocked': 'accès coupé',
  'share.unblock': 'Débloquer',
  'share.unblocking': 'Levée du blocage…',
  'share.action.share': 'partagé',
  'share.action.revoke': 'révoqué',
  'share.action.sovereign_remove': 'accès coupé',
  'share.action.member_add': 'membre ajouté',
  'share.action.member_remove': 'membre retiré',
  'share.action.leave': 'sortie du groupe',

  'me.title': 'Moi',

  // ── Corbeille ─────────────────────────────────────────────────────────────
  // Le mot est **le même que celui du geste** : on supprime, ça va à la
  // corbeille, on restaure. Trois mots, et aucun jargon — ni « archive », ni
  // « rétention », ni « purge », qui ne disent rien à qui vient de perdre une
  // photo.
  'trash.title': 'Corbeille',
  'trash.loading': 'Ouverture de la corbeille…',
  'trash.restoring': 'Restauration…',
  'trash.empty': 'Rien à restaurer : la corbeille est vide.',
  'trash.window': 'Ce qui est supprimé reste ici {days} jours, puis disparaît pour de bon. Les fichiers occupent encore votre espace jusque-là.',
  'trash.windowSoft': 'Ce qui est supprimé reste ici un temps, puis disparaît pour de bon. Les fichiers occupent encore votre espace jusque-là.',
  'trash.purgeAt': 'disparaît le {date}',
  'trash.isShare': 'partage — le restaurer rend aussi son audience',
  'trash.restore': 'Restaurer',

  // ── Administration ────────────────────────────────────────────────────────
  // La console n'existait pas : le socle savait créer un compte depuis l'étape 1
  // et aucun client ne l'exposait, si bien que 966 comptes de test se sont
  // accumulés sans que personne puisse s'en apercevoir.
  'admin.title': 'Administration',
  // **R5 — le rattrapage des descriptions.** Compter d'abord : c'est la seule
  // question à se poser avant un geste qui coûte un appel au modèle par photo.
  'admin.describeTitle': 'Descriptions automatiques',
  'admin.describeHelp': 'Décrit les photos et vidéos déjà envoyées qui ne l’ont jamais été. Ce qu’un utilisateur a corrigé n’est jamais touché. Les descriptions arrivent au fil de la file, pas d’un coup.',
  'admin.describeUpgrade': 'Refaire aussi celles décrites par un prompt plus ancien',
  'admin.describeCount': 'Compter',
  'admin.describeRun': 'Décrire',
  'admin.describeResume': 'Reprendre',
  'admin.describeCounting': 'Comptage des photos à décrire…',
  'admin.describeQueuing': 'Mise en file des descriptions…',
  'admin.describeCounted': '{selected} à décrire sur {scanned} photos et vidéos.',
  'admin.describeQueued': '{enqueued} descriptions en file sur {scanned} photos et vidéos.',
  'admin.describeUnfinished': 'Parcours interrompu — « Reprendre » le poursuit.',
  'admin.visualHashTitle': 'Empreintes',
  'admin.visualHashHelp': 'Calcule les empreintes qui manquent aux photos et vidéos déjà envoyées : l’empreinte visuelle, qui signale les doublons probables, et l’empreinte exacte, qui permet à l’app de libérer la place du téléphone. Les originaux sont relus une fois — y compris ceux déjà archivés, ce qui se paie (quelques centimes par dizaine de gigaoctets). Les empreintes arrivent au fil de la file.',
  'admin.visualHashRun': 'Calculer',
  'admin.visualHashCounting': 'Comptage des photos sans empreinte…',
  'admin.visualHashQueuing': 'Mise en file des empreintes…',
  'admin.visualHashCounted': '{selected} sans empreinte sur {scanned} photos et vidéos.',
  'admin.visualHashQueued': '{enqueued} empreintes en file sur {scanned} photos et vidéos.',
  'admin.dashboard': 'Tableau de bord',
  'admin.accounts': 'Comptes',
  'admin.business': 'Objets métier',
  'admin.technical': 'Objets techniques',
  'admin.approx': 'Mesures approchées : la table est rafraîchie toutes les six heures, les métriques S3 une fois par jour, la profondeur de file est approximative par construction.',
  'admin.measuredAt': 'mesuré le {at}',
  // **Pas de date quand la source n'en donne pas.** `DescribeTable` ne dit pas
  // quand il a compté ; afficher l'heure de lecture faisait croire à une mesure
  // fraîche sur une valeur vieille de plusieurs heures.
  'admin.lagging': 'rafraîchi par DynamoDB, avec plusieurs heures de retard',
  'admin.readAt': 'lu le {at}',
  'admin.refresh': 'Rafraîchir',
  'admin.refreshing': 'Lecture…',
  'admin.users': 'Comptes',
  'admin.active': 'Actifs',
  'admin.disabled': 'Désactivés',
  'admin.admins': 'Administrateurs',
  'admin.entities': 'Objets',
  'admin.collections': 'Partages',
  'admin.groups': 'Cercles',
  'admin.stored': 'Stocké',
  'admin.incomplete':
    '{count} compte sans utilisateur Cognito — la création s’est arrêtée en chemin, l’espace est inerte et la créer à nouveau reprendra où elle s’est arrêtée.|{count} comptes sans utilisateur Cognito — la création s’est arrêtée en chemin, leur espace est inerte et les créer à nouveau reprendra où elle s’est arrêtée.',
  'admin.cognitoUsers': 'Comptes Cognito',
  'admin.drift': 'écart de {count} avec les profils',
  'admin.tableItems': 'Items de table',
  'admin.tableBytes': 'Taille de table',
  'admin.bucketObjects': 'Objets S3',
  'admin.bucketBytes': 'Octets S3',
  'admin.queue': 'File des dérivés',
  'admin.inFlight': '{count} en cours de traitement',
  'admin.noStats': 'Aucune mesure disponible.',
  'admin.noAccounts': 'Aucun compte.',
  'admin.newAccount': 'Nouveau compte',
  'admin.email': 'Adresse',
  'admin.alias': 'Nom affiché',
  'admin.role': 'Rôle',
  'admin.role.user': 'Utilisateur',
  'admin.role.admin': 'Administrateur',
  'admin.create': 'Créer',
  'admin.creating': 'Création du compte…',
  'admin.emailNotStored': 'L’adresse sert à envoyer le mot de passe temporaire. Elle n’est pas conservée : seuls le nom affiché et l’identifiant reviendront ici.',
  'admin.ownsEntities': '{count} objet|{count} objets',
  'admin.ownsAlbums': '{count} partage|{count} partages',
  'admin.disable': 'Désactiver',
  'admin.confirmDisable': 'Couper l’accès',
  'admin.disabling': 'Coupure de l’accès…',
  'admin.enable': 'Réactiver',
  'admin.enabling': 'Réactivation…',
  'admin.resetPassword': 'Renvoyer le mot de passe',
  'admin.resetting': 'Envoi du courrier…',
  'admin.resetInvitationSent': 'Invitation renvoyée',
  'admin.resetCodeSent': 'Code envoyé',
  'admin.off': 'Désactivé',
  'admin.unlinked': 'Sans Cognito',
  'admin.you': 'vous',
  'admin.sudoNeeded': 'Cette action demande une authentification récente.',
  'admin.reauthenticate': 'Ressaisir le mot de passe',
  'me.identity': 'Identité',
  'me.alias': 'Nom affiché',
  'me.uid': 'Identifiant',
  'me.storage': 'Stockage',
  'me.used': '{used} utilisés sur {quota}',
  'me.deleted': 'dont {size} en corbeille',
  'me.preferences': 'Préférences',
  'me.theme': 'Thème',
  'me.language': 'Langue',
  'me.serverTime': 'Heure du serveur',
  'me.admin': 'Compte administrateur',
  'me.devicesSoon':
    'La liste des appareils demande des routes qui n’existent pas encore.',
  'me.aliasPlaceholder': 'Votre nom, tel que les autres le verront',
  'me.saveAlias': 'Enregistrer',
  'me.savingAlias': 'Enregistrement du nom…',
  'me.changeAvatar': 'Changer de photo de profil',
  'me.savingAvatar': 'Envoi de la photo de profil…',

  'error.avatarFormat': 'Ce fichier n’est pas une image JPEG, PNG ou WebP.',
  'error.avatarPixels': 'Cette image est trop grande en pixels — 50 mégapixels au maximum.',
  // Partagé avec l'envoi de fichiers : le plafond dépend du type, donc la phrase
  // ne le nomme pas — le dire faux serait pire que de ne pas le dire.
  'error.tooLarge': 'Ce fichier dépasse la taille autorisée.',
  'error.folderNotEmpty': 'Ce dossier n’est pas vide.',
  'error.folderRoot': 'La racine ne se supprime pas.',
  'error.folderUnknown': 'Ce dossier n’existe pas — créez-le d’abord.',
  'error.folderInvalid': 'Chemin invalide : commencez par « / », sans segment vide.',
  'error.folderTooDeep': 'Chemin trop profond (10 niveaux au maximum).',
  'error.folderTooLong': 'Chemin trop long.',
  'error.folderLimit': 'Vous avez atteint le nombre maximal de dossiers.',
  'error.nameRequired': 'Un nom est nécessaire.',
  'error.quotaExceeded': 'Votre espace de stockage est plein.',
  'error.tagTooLong': 'Ce mot-clé est trop long.',
  'error.tooManyTags': 'Trop de mots-clés pour un seul objet.',
  'error.descriptionTooLong': 'Cette description est trop longue.',
  'error.describeDisabled': 'La description automatique est coupée pour le moment.',
  'error.notReady': 'Cet objet est encore en traitement.',
  'error.generic': 'Erreur {status} ({code}).',
  'error.albumDeleteLater': 'un partage se supprime depuis son écran',
  'error.noUploadUrl': 'aucune URL d’envoi rendue pour le rôle source',
};

const en: Record<string, string> = {
  'app.name': 'Synkara',
  'app.tagline':
    'A space of your own for photos, documents and notes. What you share goes to circles you name — and you can take it back.',
  'app.promiseHead': 'Everything you keep, and',
  'app.promiseTail': 'who can see it.',
  'app.signIn': 'Sign in',
  'app.signOut': 'Sign out',
  'app.loadingAccount': 'Loading your account…',
  'app.signingIn': 'Signing in…',
  'app.syncing': 'Syncing…',
  'app.syncingCount': 'Syncing — {count} item received|Syncing — {count} items received',
  'app.sessionExpired': 'Session expired.',
  'app.cancel': 'Cancel',

  'nav.space': 'My space',
  'nav.space.q': 'Where did I file it?',
  'nav.shared': 'My shares',
  'nav.shared.q': 'What is circulating, and with whom?',
  'nav.me': 'Me',
  'nav.back': 'Back',

  'header.storage': 'Storage',
  'header.refresh': 'Refresh',
  'header.refreshing': 'Looking for what’s new…',
  'header.theme.light': 'Light theme',
  'header.theme.dark': 'Dark theme',
  'header.theme.system': 'System theme',
  'header.language': 'Language',
  'header.identifier': 'Identifier',
  'header.account': '{name}’s account',

  'state.offline': 'Offline — you are seeing the local copy; changes will wait.',
  'state.emptyFolder': 'This folder is empty.',
  'state.emptyGallery': 'No files yet.',
  'state.emptyGroups': 'You are not a member of any group.',
  'state.emptyFeed': 'Nothing shared yet.',

  'add.label': 'Add',
  'add.camera': 'Take a photo',
  'add.media': 'Photos and videos',
  'add.documents': 'Documents',
  'space.root': 'Root',
  'space.folders': 'Folders',
  'space.newFolder': 'New folder',
  'space.newNote': 'New note',
  'space.deleteFolder': 'Delete this folder',
  'space.axis': 'Way of looking',
  'space.axis.folder': 'Folders',
  'space.axis.all': 'Everything',
  'space.axis.allQ': 'What do I have?',
  'space.chooseFolder': 'Folder: {name} — change folder',
  'space.display': 'Display',
  'space.displayCycle': 'Display: {current} — change',
  'space.layout': 'Layout',
  'space.densityGroup': 'Thumbnail size',
  'space.countDetail': '{own} here, {total} including subfolders',
  'space.density.large': 'Large thumbnails',
  'space.density.medium': 'Medium thumbnails',
  'space.density.small': 'Small thumbnails',
  'space.layout.auto': 'Automatic',
  'space.layout.list': 'As a list',
  'space.layout.grid': 'As thumbnails',
  'space.expand': 'Expand {name}',
  'space.collapse': 'Collapse {name}',
  'space.folderPlaceholder': '/holidays/2026',
  'space.notePlaceholder': 'Note name',
  'space.create': 'Create',
  'space.upload': 'Upload files',
  'space.delete': 'Delete',
  'space.confirmDelete': 'Delete “{name}”? It goes to the trash, where it stays {days} days.',
  'space.confirmDeleteSoft': 'Delete “{name}”? It goes to the trash.',
  'space.creatingFolder': 'Creating folder…',
  'space.creatingNote': 'Creating note…',
  'space.deletingFolder': 'Deleting folder…',
  'space.deleting': 'Deleting…',
  'space.uploading': 'Uploading…',
  'space.checkingDuplicates': 'Looking for duplicates ({index}/{total})…',
  'import.duplicates':
    '{count} duplicate was found and will not be imported.|{count} duplicates were found and will not be imported.',
  'import.duplicatesInTrash': 'Including {count} in the trash.',
  'import.duplicatesTitle': 'Duplicates found',
  'import.skipDuplicates': 'OK',
  'import.force': 'Import anyway',
  'space.uploadingOne': 'Uploading “{name}” ({index}/{total})…',
  'space.uploadingBytes': 'Uploading “{name}” ({index}/{total}) — {sent} of {size}…',
  'space.resumingOne': 'Resuming upload of “{name}” ({index}/{total})…',
  'space.processingCount': '{count} file still processing…|{count} files still processing…',

  'viewer.close': 'Close',
  'viewer.previous': 'Previous',
  'viewer.next': 'Next',
  'viewer.unavailable': 'These bytes are not available.',
  'viewer.fullscreen': 'Full screen',
  'viewer.exitFullscreen': 'Leave full screen',

  'reader.emptyNote': 'This note is empty — it has no content yet.',
  'reader.noBytes': 'This document has no observed bytes yet.',
  'reader.notPreviewable': 'No preview for a {format} file.',

  'editor.write': 'Write',
  'editor.leaveUnsaved': 'Your changes could not be saved.',
  'editor.leaveDiscard': 'Close without saving',
  'editor.stay': 'Stay',
  'editor.body': 'Note body',
  'editor.modeRich': 'Rich editing',
  'editor.modeSource': 'Show Markdown',
  'editor.format': 'Formatting',
  'editor.more': 'More actions',
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.strike': 'Strikethrough',
  'editor.heading': 'Heading {level}',
  'editor.paragraph': 'Paragraph',
  'editor.bulletList': 'Bulleted list',
  'editor.numberList': 'Numbered list',
  'editor.todo': 'Checkbox',
  'editor.quote': 'Quote',
  'editor.codeBlock': 'Code block',
  'editor.rule': 'Divider',
  'editor.link': 'Link',
  'editor.linkUrl': 'Link address',
  'editor.linkApply': 'Apply',
  'editor.linkRemove': 'Remove link',
  'editor.table': 'Table',
  'editor.tableTools': 'Table',
  'editor.rowBefore': 'Row above',
  'editor.rowAfter': 'Row below',
  'editor.colBefore': 'Column to the left',
  'editor.colAfter': 'Column to the right',
  'editor.dropRow': 'Delete row',
  'editor.dropCol': 'Delete column',
  'editor.dropTable': 'Delete table',
  'editor.image': 'Image',
  'editor.imageTools': 'Image',
  'editor.imageFromSpace': 'Choose from my space',
  'editor.imageUpload': 'Upload a photo',
  'editor.imageAlt': 'Alternative text',
  'editor.imageAltHint': 'Describe the image…',
  'editor.imageRemove': 'Remove image',
  'editor.imageLoading': 'Loading your photos…',
  'editor.imageNone': 'No photos in your space yet.',
  'editor.imagePickFailed': 'Your photos could not be loaded.',
  'editor.opening': 'Opening note…',
  'editor.save': 'Save',
  'editor.saving': 'Saving…',
  'editor.saved': 'Saved',
  'editor.unsaved': 'Unsaved',
  'editor.revision': 'Revision {rev}',
  'editor.reload': 'Reload',
  'editor.reloading': 'Reloading…',
  'editor.retry': 'Retry',
  'editor.keepMine': 'Keep my text',
  'editor.history': 'History',
  'editor.historyEmpty': 'No revisions: this note has never been saved.',
  'editor.loadingHistory': 'Loading history…',
  'editor.restore': 'Restore',
  'editor.restoring': 'Restoring…',
  'editor.conflictStale':
    'This note was saved elsewhere (revision {rev}). Reload, or keep your text to save over it.',
  'editor.conflictLocked': 'Someone is editing this note right now.',
  'editor.conflictLockedUntil': 'Someone is editing this note; retry after {at}.',
  'editor.conflictLockLost':
    'Your lock was taken during the save (revision {rev}). Reload before retrying.',
  'editor.conflictMissingBytes': 'The server did not observe the uploaded bytes. Retry the save.',

  'inspector.title': 'Details',
  'inspector.unavailable': 'Details unavailable.',
  'inspector.name': 'Name',
  'inspector.rename': 'Rename',
  'inspector.renaming': 'Renaming…',
  'inspector.folder': 'Folder',
  'inspector.moving': 'Moving…',
  'inspector.kind': 'Kind',
  'inspector.owner': 'Owner',
  'inspector.ownerMe': 'You',
  'inspector.ownerOther': 'Someone else',
  'inspector.date': 'Date',
  'inspector.uploaded': 'Added on',
  'inspector.camera': 'Camera',
  'inspector.location': 'Location',
  'inspector.size': 'Size',
  'inspector.format': 'Format',
  'inspector.revision': 'Revision',
  'inspector.pending': 'pending',
  'inspector.download': 'Download',
  'inspector.role.standard': 'Screen version',
  'inspector.role.source': 'Original file',
  'inspector.role.content': 'Note text',
  'inspector.roleArchived': 'archived',
  'inspector.preparing': 'Preparing download…',
  'inspector.votes': '{count} rating|{count} ratings',
  'inspector.open': 'Open',

  'inspector.description': 'Description',
  'inspector.descriptionPlaceholder': 'Add a description',
  'inspector.tags': 'Keywords',
  'inspector.tagAdd': 'Add a keyword',
  'inspector.tagRemove': 'Remove “{tag}”',
  'inspector.save': 'Save',
  'inspector.saving': 'Saving…',
  'inspector.machine': 'described automatically',
  'describe.regenerate': 'Regenerate description',
  'describe.requesting': 'Requesting a description…',
  'describe.confirm': 'Replace your corrections?',
  'describe.confirmNote': 'The description and keywords you edited will be replaced by the model’s.',
  'describe.confirmVerb': 'Replace',
  'describe.queued': 'Description requested: it will arrive in a few moments.',
  'describe.pending': 'Not described yet.',

  'poster.title': 'Poster',
  'poster.help': 'The frame that stands for this video, here and for everyone you share it with.',
  'poster.frame': 'Frame {n}',
  'poster.reset': 'First frame',
  'poster.saving': 'Saving poster…',

  'kind.note': 'note',
  'kind.doc': 'document',
  'kind.photo': 'photo',
  'kind.video': 'video',
  'kind.collection': 'share · {count} item|share · {count} items',

  'gallery.filters': 'Filters',
  'gallery.type': 'Type',
  'gallery.any': 'All',
  'gallery.images': 'Photos',
  'gallery.videos': 'Videos',
  'gallery.notes': 'Notes',
  'gallery.docs': 'Documents',
  'gallery.minScore': 'Minimum rating',
  'gallery.camera': 'Camera',
  'gallery.share': 'Share',
  'gallery.shareNone': 'In no share',
  'gallery.owner': 'Owner',
  'gallery.ownerMe': 'Me',
  'gallery.ownerUnknown': 'Unnamed',
  'gallery.emoji': 'Reactions',
  'gallery.sort': 'Sort',
  'gallery.sortNewest': 'Newest first',
  'gallery.sortOldest': 'Oldest first',
  'gallery.sortKind': 'Type, then name',
  'gallery.sortName': 'Name',
  'gallery.sortSize': 'Size',
  'gallery.duplicates': 'Duplicates',
  'gallery.duplicatesOn': 'Duplicates ({count})',
  'gallery.duplicateThreshold': 'Similarity: {n}',
  'gallery.duplicateThresholdHint': '0–2: true duplicates · above: similar photos',
  'gallery.duplicateOf': 'Likely duplicate — {count} photo in the group|Likely duplicate — {count} photos in the group',
  'gallery.clearFilters': 'Clear filters',
  'gallery.atLeast': '{count} star and up|{count} stars and up',
  'gallery.count': '{shown} of {total}',
  'gallery.localScope': 'filters apply to what is synchronised',
  'search.label': 'Search',
  'search.placeholder': 'Search: dog, beach, août 2026…',
  'search.none': 'Nothing here matches “{query}”.',
  'search.limit': 'Search covers what is synchronised, and the words written — descriptions are in French: “seaside” does not find “plage”.',
  'gallery.pick': 'Select',
  'gallery.inspect': 'Details',
  'send.action': 'Send',
  'send.preparing': 'Preparing…',
  'send.expired': 'The share sheet did not open in time. Try again — the data is ready now.',
  'send.unsupported': 'This browser cannot send this file.',
  'send.noBytes': 'Could not fetch the bytes ({status}).',
  'send.failed': 'Sending failed ({code}).',
  'news.title': 'What’s new',
  'news.lead':
    'What has changed in Synkara, newest first. You are on version {version}.',
  'send.link': 'Send a link',
  'send.linkWhich': 'Which share should the link open?',
  'link.opening': 'Opening the link…',
  'link.unavailable':
    'This link leads to nothing you can see: the share may have been removed, or is not open to you.',
  'link.photoGone': 'This photo is no longer in this share.',
  'gallery.selection': 'Selection',
  'gallery.selectAll': 'Select all ({count})',
  'gallery.picked': '{count} selected',
  'gallery.clearSelection': 'Clear selection',
  'gallery.deletePicked': 'Delete',
  'gallery.moreActions': 'More actions',
  'gallery.confirmDeletePicked':
    'Delete {count} item? It goes to the trash, where it stays {days} days.|Delete {count} items? They go to the trash, where they stay {days} days.',
  'gallery.confirmDeletePickedSoft':
    'Delete {count} item? It goes to the trash.|Delete {count} items? They go to the trash.',
  'gallery.deletingSelection': 'Deleting {count} item…|Deleting {count} items…',
  'gallery.noThumb': 'Thumbnail unavailable',

  'collection.title': 'Share',
  'collection.loading': 'Opening share…',
  'collection.creating': 'Creating share…',
  'collection.adding': 'Adding to share…',
  'collection.removing': 'Removing from share…',
  'collection.empty': 'This share is empty.',
  'collection.addTo': 'Add to a share…',
  'object.actions': 'Actions on {name}',
  'collection.add': 'Add',
  'collection.newName': 'New share',
  'collection.createAndAdd': 'Create and add',
  'collection.pickGroup': 'Which group?',
  'collection.backToGroups': 'All groups',
  'collection.shareCount': '{count} share|{count} shares',
  'collection.lastUsed': 'Last used',
  'collection.inShares': 'Already in these shares',
  'collection.addToExisting': 'Add to an existing share',
  'collection.addToAnother': 'Add to another existing share',
  'collection.createSection': 'Create a new share',
  'collection.allAlreadyIn': 'Every share in this group already has them.',
  'collection.addedTo': 'Done — {count} share|Done — {count} shares',
  'collection.remove': 'Remove',
  'collection.removeIsNotDelete':
    'Removing an item from a share does not delete it: it stays filed in its folder.',
  'collection.confirmRemove': 'Remove this item from the share?',
  'collection.someRefused': '{count} item refused ({code}).|{count} items refused ({code}).',
  'collection.needsGroup': 'A share always goes to a group: pick one.',
  'collection.group': 'Shared with',
  'collection.none': 'No share in this group.',
  'collection.itemCount': '{count} item|{count} items',
  'collection.deleting': 'Deleting share…',
  'collection.delete': 'Delete share',
  'collection.confirmDelete':
    'Delete this share? Members lose access to what it exposed; the objects themselves stay in your space.',
  'collection.savingPolicy': 'Saving…',
  'collection.contribToggle': 'Let members contribute',
  'collection.contribRestrict': 'Restrict adding to the owner',
  'collection.admin': 'Manage this share',
  'collection.contribOn': 'Members can add their own items and edit its notes.',
  'collection.contribOff': 'Only you can add items here.',
  'collection.collectiveNotice':
    'Collective share: you can add your own items and edit its notes.',
  'collection.collective': 'Collective',
  'collection.readOnlyNote': 'This note is not open to you for writing.',

  'reaction.title': 'Reactions',
  'reaction.score': 'Rating',
  'reaction.emoji': 'Emoji',
  'reaction.stars': '{count} star|{count} stars',
  'reaction.average': 'Average rating',
  'reaction.clear': 'Remove my reaction',
  'reaction.saving': 'Saving reaction…',
  'reaction.removing': 'Removing reaction…',
  'reaction.who': 'Who reacted',
  'reaction.loadingPeople': 'Loading reactions…',
  'reaction.nobody': 'Nobody has reacted yet.',
  'reaction.moderate': 'Remove {name}’s reaction',
  'reaction.moderating': 'Removing reaction…',

  'entity.pendingUpload': 'upload pending',
  'entity.processing': 'processing',
  'entity.failed': 'processing failed',

  'fail.pixels_exceeded':
    'Image too large to process: it exceeds the resolution limit. A smaller version will go through.',
  'fail.format_unknown':
    'Unrecognised format: these bytes match no known image or video type.',
  'fail.format_rejected':
    'Format rejected for this kind of file — for instance a video sent down the image path.',
  'fail.dimensions_unknown': 'Unreadable dimensions: the file may be truncated or incomplete.',
  'fail.derivatives_failed':
    'Processing failed on the server. The source is intact: “Retry” usually settles it.',
  'fail.unknown': 'Reason: {reason}',
  'entity.failedBecause': '{label} — {reason}',

  'entity.retry': 'Retry processing',
  'entity.retrying': 'Retrying…',
  'entity.retryHint': 'The bytes are already uploaded — only the derivatives are rebuilt.',

  'shared.members': '{count} member|{count} members',
  'shared.role.owner': 'owner',
  'shared.role.member': 'member',

  'group.new': 'Create group',
  'group.newName': 'Group name',
  'group.creating': 'Creating group…',
  'group.rename': 'Rename group',
  'group.renaming': 'Renaming…',
  'group.delete': 'Delete group',
  'group.deleting': 'Deleting group…',
  'group.confirmDelete': 'The group and every member’s access disappear. This is permanent.',
  'group.leave': 'Leave group',
  'group.leaving': 'Leaving…',
  'group.confirmLeave': 'You will lose access to everything shared there.',
  'group.collections': 'Shares',
  'group.collectionCount': '{count} share|{count} shares',
  'group.feed': 'What’s new',
  'group.itemCount': '{count} item|{count} items',
  'group.fresh': '{count} this week',
  'group.seeAll': 'See all',
  'group.members': 'Members',
  'group.invite': 'Invite',
  'group.invitePlaceholder': 'Email address',
  'group.inviting': 'Inviting…',
  'group.remove': 'Remove {name} from the group',
  'group.removing': 'Removing…',
  'group.confirmRemove': 'Remove',
  'group.unnamed': 'Unnamed',
  'group.you': 'you',
  'group.someone': 'Someone',
  'group.emptyFeed': 'Nothing has been shared in this group yet.',
  'group.aloneHere': 'You are alone in this group.',
  'group.revoked': 'removed',
  'group.more': 'Show more',

  'share.where': 'Where this goes',
  'share.loading': 'Loading shares…',
  'share.nowhere': 'This object is not shared anywhere.',
  'share.viaDirect': 'direct access',
  'share.viaCollection': 'through a share',
  'share.revoke': 'Cut off {name}',
  'share.revoking': 'Cutting access…',
  'share.exposedTo': 'Shared with {count} group|Shared with {count} groups',
  'share.blocked': 'access cut',
  'share.unblock': 'Unblock',
  'share.unblocking': 'Unblocking…',
  'share.action.share': 'shared',
  'share.action.revoke': 'revoked',
  'share.action.sovereign_remove': 'access cut',
  'share.action.member_add': 'member added',
  'share.action.member_remove': 'member removed',
  'share.action.leave': 'left the group',

  'me.title': 'Me',

  // ── Trash ─────────────────────────────────────────────────────────────────
  'trash.title': 'Trash',
  'trash.loading': 'Opening the trash…',
  'trash.restoring': 'Restoring…',
  'trash.empty': 'Nothing to restore: the trash is empty.',
  'trash.window': 'Deleted items stay here for {days} days, then go for good. Files still take up your space until then.',
  'trash.windowSoft': 'Deleted items stay here for a while, then go for good. Files still take up your space until then.',
  'trash.purgeAt': 'goes for good on {date}',
  'trash.isShare': 'share — restoring it also restores who could see it',
  'trash.restore': 'Restore',

  // ── Administration ────────────────────────────────────────────────────────
  'admin.title': 'Administration',
  'admin.describeTitle': 'Automatic descriptions',
  'admin.describeHelp': 'Describes photos and videos already uploaded that never were. What a user corrected is never touched. Descriptions arrive as the queue drains, not all at once.',
  'admin.describeUpgrade': 'Also redo those described by an older prompt',
  'admin.describeCount': 'Count',
  'admin.describeRun': 'Describe',
  'admin.describeResume': 'Resume',
  'admin.describeCounting': 'Counting photos to describe…',
  'admin.describeQueuing': 'Queuing descriptions…',
  'admin.describeCounted': '{selected} to describe out of {scanned} photos and videos.',
  'admin.describeQueued': '{enqueued} descriptions queued out of {scanned} photos and videos.',
  'admin.describeUnfinished': 'Sweep interrupted — “Resume” carries on.',
  'admin.visualHashTitle': 'Fingerprints',
  'admin.visualHashHelp': 'Computes the fingerprints missing from photos and videos already uploaded: the visual one, which flags likely duplicates, and the exact one, which lets the app free up space on the phone. Originals are read once — including archived ones, which costs a little (a few cents per ten gigabytes). Fingerprints arrive as the queue drains.',
  'admin.visualHashRun': 'Compute',
  'admin.visualHashCounting': 'Counting photos without a fingerprint…',
  'admin.visualHashQueuing': 'Queuing fingerprints…',
  'admin.visualHashCounted': '{selected} without a fingerprint out of {scanned} photos and videos.',
  'admin.visualHashQueued': '{enqueued} fingerprints queued out of {scanned} photos and videos.',
  'admin.dashboard': 'Dashboard',
  'admin.accounts': 'Accounts',
  'admin.business': 'Business objects',
  'admin.technical': 'Technical objects',
  'admin.approx': 'Approximate measures: the table is refreshed every six hours, S3 metrics once a day, and queue depth is approximate by design.',
  'admin.measuredAt': 'measured {at}',
  'admin.lagging': 'refreshed by DynamoDB, hours behind',
  'admin.readAt': 'read {at}',
  'admin.refresh': 'Refresh',
  'admin.refreshing': 'Reading…',
  'admin.users': 'Accounts',
  'admin.active': 'Active',
  'admin.disabled': 'Disabled',
  'admin.admins': 'Administrators',
  'admin.entities': 'Objects',
  'admin.collections': 'Shares',
  'admin.groups': 'Circles',
  'admin.stored': 'Stored',
  'admin.incomplete':
    '{count} account without a Cognito user — creation stopped midway, the space is inert, and creating it again resumes where it stopped.|{count} accounts without a Cognito user — creation stopped midway, their spaces are inert, and creating them again resumes where it stopped.',
  'admin.cognitoUsers': 'Cognito accounts',
  'admin.drift': '{count} apart from profiles',
  'admin.tableItems': 'Table items',
  'admin.tableBytes': 'Table size',
  'admin.bucketObjects': 'S3 objects',
  'admin.bucketBytes': 'S3 bytes',
  'admin.queue': 'Derivatives queue',
  'admin.inFlight': '{count} in flight',
  'admin.noStats': 'No measurement available.',
  'admin.noAccounts': 'No account.',
  'admin.newAccount': 'New account',
  'admin.email': 'Email',
  'admin.alias': 'Display name',
  'admin.role': 'Role',
  'admin.role.user': 'User',
  'admin.role.admin': 'Administrator',
  'admin.create': 'Create',
  'admin.creating': 'Creating account…',
  'admin.emailNotStored': 'The address is used to send the temporary password. It is not kept: only the display name and the identifier will come back here.',
  'admin.ownsEntities': '{count} object|{count} objects',
  'admin.ownsAlbums': '{count} share|{count} shares',
  'admin.disable': 'Disable',
  'admin.confirmDisable': 'Cut access',
  'admin.disabling': 'Cutting access…',
  'admin.enable': 'Re-enable',
  'admin.enabling': 'Re-enabling…',
  'admin.resetPassword': 'Resend password',
  'admin.resetting': 'Sending the email…',
  'admin.resetInvitationSent': 'Invitation resent',
  'admin.resetCodeSent': 'Code sent',
  'admin.off': 'Disabled',
  'admin.unlinked': 'No Cognito',
  'admin.you': 'you',
  'admin.sudoNeeded': 'This action requires a recent sign-in.',
  'admin.reauthenticate': 'Re-enter password',
  'me.identity': 'Identity',
  'me.alias': 'Display name',
  'me.uid': 'Identifier',
  'me.storage': 'Storage',
  'me.used': '{used} used of {quota}',
  'me.deleted': 'including {size} in the trash',
  'me.preferences': 'Preferences',
  'me.theme': 'Theme',
  'me.language': 'Language',
  'me.serverTime': 'Server time',
  'me.admin': 'Administrator account',
  'me.devicesSoon': 'The device list needs routes that do not exist yet.',
  'me.aliasPlaceholder': 'Your name, as others will see it',
  'me.saveAlias': 'Save',
  'me.savingAlias': 'Saving your name…',
  'me.changeAvatar': 'Change profile picture',
  'me.savingAvatar': 'Uploading profile picture…',

  'error.avatarFormat': 'This file is not a JPEG, PNG or WebP image.',
  'error.avatarPixels': 'This image has too many pixels — 50 megapixels maximum.',
  'error.tooLarge': 'This file is over the allowed size.',
  'error.folderNotEmpty': 'This folder is not empty.',
  'error.folderRoot': 'The root cannot be deleted.',
  'error.folderUnknown': 'This folder does not exist — create it first.',
  'error.folderInvalid': 'Invalid path: start with “/”, with no empty segment.',
  'error.folderTooDeep': 'Path too deep (10 levels at most).',
  'error.folderTooLong': 'Path too long.',
  'error.folderLimit': 'You have reached the maximum number of folders.',
  'error.nameRequired': 'A name is required.',
  'error.quotaExceeded': 'Your storage is full.',
  'error.tagTooLong': 'This keyword is too long.',
  'error.tooManyTags': 'Too many keywords for one item.',
  'error.descriptionTooLong': 'This description is too long.',
  'error.describeDisabled': 'Automatic description is switched off for now.',
  'error.notReady': 'This item is still being processed.',
  'error.generic': 'Error {status} ({code}).',
  'error.albumDeleteLater': 'a share is deleted from its own screen',
  'error.noUploadUrl': 'no upload URL returned for the source role',
};

/**
 * Les deux tables, **exportées pour être mesurées**.
 *
 * Un mot d'écran ne se garde pas en le relisant : il se garde par une mesure qui
 * parcourt les chaînes. C'est la seule raison de cet export — le code de l'app,
 * lui, passe par `t()`.
 */
export const dictionaries: Record<Locale, Record<string, string>> = { fr, en };

const storedLocale = ((): Locale => {
  const raw = localStorage.getItem(KEY);
  return raw === 'fr' || raw === 'en' ? raw : 'fr';
})();

export const locale = signal<Locale>(storedLocale);

effect(() => {
  localStorage.setItem(KEY, locale.value);
  document.documentElement.lang = locale.value;
});

/**
 * **Le pluriel, et pourquoi il n'y avait pas de bibliothèque à ajouter.**
 *
 * Vingt et une chaînes s'écrivaient « {count} membre(s) », « élément(s)
 * refusé(s) », « 1 compte(s) sans utilisateur Cognito ». La parenthèse est le
 * signe qu'on a renoncé : elle fait porter au lecteur un arbitrage que la machine
 * connaît, et « 1 compte(s) » est simplement faux.
 *
 * La forme retenue est un séparateur `|` dans la valeur : `singulier|pluriel`. Ce
 * n'est pas un choix de paresse mais de portée — les deux langues du produit ont
 * la même **arité** (deux formes) et ne diffèrent que par le seuil :
 *
 * - **français** : 0 et 1 au singulier (« 0 membre », « 1 membre ») ;
 * - **anglais** : 1 seul au singulier (« 0 members », « 1 member »).
 *
 * Une langue à trois formes — le polonais, l'arabe — demanderait `Intl.PluralRules`
 * et une table par catégorie. Le jour où elle arrive, c'est cette fonction qui
 * change, et aucune des chaînes : elles portent déjà leurs variantes.
 *
 * La variante est choisie sur `count`, quand il existe et qu'il est un nombre.
 * Une chaîne sans `|` traverse sans rien subir.
 *
 * Lire `locale.value` ici suffit à rendre **tout composant qui appelle `t`**
 * réactif au changement de langue : le signal note sa dépendance pendant le
 * rendu, et Preact redessine ce qui en dépend, rien de plus.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const raw = dictionaries[locale.value][key] ?? dictionaries.fr[key] ?? key;
  const picked = plural(raw, vars?.['count']);
  if (!vars) return picked;
  return picked.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`));
}

/** `singulier|pluriel` → la forme qui va avec `count`. Sans `|`, rien ne bouge. */
function plural(raw: string, count: string | number | undefined): string {
  const cut = raw.indexOf('|');
  if (cut < 0) return raw;
  const n = typeof count === 'number' ? count : Number(count);
  // Un `count` absent ou illisible prend le **pluriel** : c'est la forme neutre
  // d'un libellé de colonne ou d'un intitulé, et « 0 » y est plus fréquent que 1.
  if (!Number.isFinite(n)) return raw.slice(cut + 1);
  const single = locale.value === 'fr' ? Math.abs(n) < 2 : Math.abs(n) === 1;
  return single ? raw.slice(0, cut) : raw.slice(cut + 1);
}

export function toggleLocale(): void {
  locale.value = locale.value === 'fr' ? 'en' : 'fr';
}
